/**
 * pixelSortPipeline — pixel sorting based on brightness (glitch art).
 *
 * Sorts pixels by luminance along horizontal or vertical bands.
 * The result is the iconic pixel-sorted glitch look. Audio drives
 * the sort threshold and band size.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface PixelSortPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * valueNoise(p);
    p = p * 2.0 + vec2(1.7, 9.2);
    a *= 0.5;
  }
  return v;
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected coords.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);

  // Generate a colorful "image" via FBM.
  vec2 ip = p * 3.0;
  vec3 col1 = vec3(0.95, 0.30, 0.10);
  vec3 col2 = vec3(0.20, 0.65, 0.95);
  vec3 col3 = vec3(0.95, 0.85, 0.30);
  vec3 imgCol = mix(col1, col2, fbm(ip + uTime * 0.2));
  imgCol = mix(imgCol, col3, fbm(ip * 2.0 - uTime * 0.3));

  // Pixel sort: we simulate by sampling nearby pixels with brightness
  // comparison. The "sort" is achieved by finding the brightest
  // neighbor in a band along the X or Y axis and using its color.
  // Audio: alternating sort direction (horizontal/vertical).
  bool horizontal = mod(floor(uTime * 0.5), 2.0) < 1.0;
  // Sort band size scales with bass.
  int bandSize = int(20.0 + bass * 80.0);
  // For each pixel, find the brightest pixel in the band.
  vec3 brightest = imgCol;
  float brightestLuma = dot(imgCol, vec3(0.299, 0.587, 0.114));
  for (int i = 1; i < 32; i++) {
    if (i >= bandSize) break;
    float offset = float(i) * 0.005;
    vec2 sampleP = horizontal ? vec2(p.x + offset, p.y) : vec2(p.x, p.y + offset);
    vec2 sampleIp = sampleP * 3.0;
    vec3 sampleCol = mix(col1, col2, fbm(sampleIp + uTime * 0.2));
    sampleCol = mix(sampleCol, col3, fbm(sampleIp * 2.0 - uTime * 0.3));
    float sampleLuma = dot(sampleCol, vec3(0.299, 0.587, 0.114));
    if (sampleLuma > brightestLuma) {
      brightest = sampleCol;
      brightestLuma = sampleLuma;
    }
  }

  // Pixel sort effect: keep only pixels above a brightness threshold.
  float threshold = 0.3 + mid * 0.3;
  float keep = smoothstep(threshold - 0.05, threshold + 0.05, brightestLuma);
  vec3 sorted = brightest;

  // Mix with the original image based on the keep threshold.
  vec3 col = mix(imgCol, sorted, keep);

  // Add chromatic aberration (RGB split).
  vec2 chromaOffset = vec2(0.005 * sin(p.y * 10.0 + uTime), 0.0);
  vec3 chromaCol = mix(col1, col2, fbm((p + chromaOffset) * 3.0 + uTime * 0.2));
  chromaCol = mix(chromaCol, col3, fbm((p + chromaOffset) * 6.0 - uTime * 0.3));
  col = mix(col, chromaCol, high * 0.4);

  // Vignette.
  float vig = 1.0 - dot(p, p) * 0.3;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computePixelSortBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.7);
  out[1] = 0.5 + 0.3 * Math.sin(t * 1.0 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.3 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.5 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.7 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 2.0 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computePixelSortRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createPixelSortPipeline(gl: WebGL2RenderingContext): PixelSortPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawPixelSort(
  gl: WebGL2RenderingContext,
  pipe: PixelSortPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
